import React from 'react';
import styled, { css } from 'styled-components';
import Container from '../container';

import MechanicalSystemIcon from '../../images/svg/mechanical-system-icon.svg';
import NaturalSystemIcon from '../../images/svg/natural-system-icon.svg';
import mq from '../../utils/mq';

const StructuredDataContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 0;
  border: 0px solid transparent;
  border-bottom: 1px solid #ebebeb;

  ${mq.mobile(css`
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 32px;
    padding: 16px 0;
  `)};
`;

const StructuredDataInfoItems = styled.div`
  display: flex;
  gap: 56px;
  row-gap: 24px;
  justify-content: flex-start;
  align-items: center;
  ${mq.tablet(css`
    flex-wrap: wrap;
    column-gap: 42px;
  `)};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 125px;
`;

const Label = styled.div`
  font-weight: bold;
  color: #161616;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
  letter-spacing: -0.32px;
`;

const Value = styled.div`
  color: #666;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.32px;
`;

const StructuredDataSystemTypes = styled.div`
  display: flex;
  gap: 56px;
  justify-content: flex-end;
  ${mq.mobile(css`
    justify-content: flex-start;
    gap: 42px;
    flex-wrap: wrap;
  `)};
`;

const SystemType = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  ${mq.mobile(css`
    gap: 12px;
  `)};
`;

const SystemTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #161616;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 100%;
    height: 100%;
  }
  ${mq.mobile(css`
    width: 32px;
    height: 32px;
  `)};
`;

const SystemTypeName = styled.div`
  font-weight: bold;
  ${mq.mobile(css`
    font-size: 15px;
  `)};
`;

type SystemTypeKey = 'mechanical' | 'natural';

interface ProjectTypeReference {
  content?: {
    title?: string;
    name?: string;
  };
  title?: string;
  name?: string;
  value?: string;
  label?: string;
  projectType?: string;
  type?: string;
}

type ProjectTypeEntry = string | ProjectTypeReference;

type ProjectTypeProp = ProjectTypeEntry | ProjectTypeEntry[] | null | undefined;

interface CaseStudyStructuredDataProps {
  propertyType: any;
  location: any;
  projectType?: ProjectTypeProp;
  projectClientName?: string;
}

const SYSTEM_TYPE_DEFINITIONS: Record<
  SystemTypeKey,
  {
    label: string;
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }
> = {
  mechanical: {
    label: 'Mechanical',
    Icon: MechanicalSystemIcon
  },
  natural: {
    label: 'Natural',
    Icon: NaturalSystemIcon
  }
};

function getProjectTypeLabel(entry: ProjectTypeEntry): string {
  if (typeof entry === 'string') {
    return entry;
  }

  const { content, title, name, value, label, projectType, type } = entry;

  if (content?.title) {
    return content.title;
  }

  if (content?.name) {
    return content.name;
  }

  if (title) {
    return title;
  }

  if (name) {
    return name;
  }

  if (value) {
    return value;
  }

  if (label) {
    return label;
  }

  if (projectType) {
    return projectType;
  }

  if (type) {
    return type;
  }

  return '';
}

function normalizeSystemTypes(projectType: ProjectTypeProp): SystemTypeKey[] {
  if (!projectType) {
    return [];
  }

  const entries = Array.isArray(projectType) ? projectType : [projectType];

  const normalized = entries
    .filter(
      (entry): entry is ProjectTypeEntry =>
        typeof entry === 'string' ||
        (typeof entry === 'object' && entry !== null)
    )
    .map(getProjectTypeLabel)
    .map((label) => label.trim().toLowerCase())
    .filter(
      (label): label is SystemTypeKey =>
        label === 'mechanical' || label === 'natural'
    );

  return Array.from(new Set(normalized));
}

export default function CaseStudyStructuredData({
  propertyType,
  location,
  projectType,
  projectClientName
}: CaseStudyStructuredDataProps) {
  const systemTypes = normalizeSystemTypes(projectType);

  return (
    <Container>
      <StructuredDataContainer>
        <StructuredDataInfoItems>
          {projectClientName && (
            <InfoItem>
              <Label>Client</Label>
              <Value>{projectClientName}</Value>
            </InfoItem>
          )}
          {propertyType && propertyType?.content?.title && (
            <InfoItem>
              <Label>Sector</Label>
              <Value>{propertyType.content.title}</Value>
            </InfoItem>
          )}
          {location && location?.content?.title && (
            <InfoItem>
              <Label>Location</Label>
              <Value>{location.content.title}</Value>
            </InfoItem>
          )}
        </StructuredDataInfoItems>

        {systemTypes.length > 0 && (
          <StructuredDataSystemTypes>
            {systemTypes.map((type) => {
              const { Icon, label } = SYSTEM_TYPE_DEFINITIONS[type];

              return (
                <SystemType key={type}>
                  <SystemTypeIcon>
                    <Icon />
                  </SystemTypeIcon>
                  <SystemTypeName>{label}</SystemTypeName>
                </SystemType>
              );
            })}
          </StructuredDataSystemTypes>
        )}
      </StructuredDataContainer>
    </Container>
  );
}
